import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { 
  Calendar as CalendarIcon, 
  Clock, 
  Users, 
  CheckCircle,
  Loader2,
  AlertCircle,
  XCircle,
  Phone,
  Mail,
  MapPin
} from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CURRENCY_SYMBOLS = { gbp: '£', usd: '$', eur: '€' };

export default function CancelBooking() {
  const { bookingId, cancellationToken } = useParams();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [cancelling, setCancelling] = useState(false);
  const [cancelled, setCancelled] = useState(false);
  const [booking, setBooking] = useState(null);
  const [restaurant, setRestaurant] = useState(null);
  const [error, setError] = useState(null);
  const [refundAmount, setRefundAmount] = useState(0);

  useEffect(() => {
    fetchBookingDetails();
  }, [bookingId, cancellationToken]);

  const fetchBookingDetails = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Fetch booking details using the cancellation endpoint
      const response = await axios.get(`${API}/booking/${bookingId}/cancel/${cancellationToken}`);
      const data = response.data;
      
      if (!data) {
        throw new Error('Booking not found');
      }
      
      // Check if already cancelled
      if (data.already_cancelled) {
        setError('This booking has already been cancelled.');
        setLoading(false);
        return;
      }
      
      setBooking(data.booking);
      setRestaurant(data.restaurant);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching booking:', err);
      setError(err.response?.data?.detail || 'Unable to load booking details. The cancellation link may be invalid or expired.');
      setLoading(false);
    }
  };

  const handleCancelBooking = async () => {
    if (!booking || !restaurant) return;
    
    setCancelling(true);
    setError(null);
    
    try {
      const response = await axios.post(`${API}/bookings/cancel/${bookingId}/${cancellationToken}`);
      
      setCancelled(true);
      setRefundAmount(response.data.refund_amount || 0);
      
      toast.success('Booking cancelled successfully', {
        description: response.data.refund_processed
          ? response.data.transaction_fee_deducted > 0
            ? 'Your deposit refund will be returned minus the original card processing fee.'
            : 'Your deposit will be refunded within 5-10 business days'
          : hasPaidDeposit
            ? 'Your booking is cancelled. In line with the deposit terms, your deposit has been kept.'
            : 'Cancellation confirmed',
      });
    } catch (err) {
      console.error('Cancellation error:', err);
      const errorMessage = err.response?.data?.detail || 'Failed to cancel booking. Please try again or contact the restaurant.';
      setError(errorMessage);
      toast.error('Cancellation failed', { description: errorMessage });
    } finally {
      setCancelling(false);
    }
  };

  const hasPaidDeposit = booking?.deposit_status === 'paid' && booking?.deposit_amount > 0;
  const depositLocked = Boolean(booking?.deposit_locked);
  const refundEligible = Boolean(booking?.refund_eligible);
  const refundHours = booking?.refund_hours_before ?? 24;
  const refundDeadlineText = booking?.refund_deadline
    ? format(new Date(booking.refund_deadline), "EEEE d MMMM yyyy 'at' HH:mm")
    : null;

  const primaryColor = restaurant?.branding?.primary_color || '#1a1f2c';
  const currencySymbol = CURRENCY_SYMBOLS[booking?.deposit_currency] || '£';
  const deductTransactionFees = Boolean(
    booking?.deduct_transaction_fees_from_refunds
      || restaurant?.deposit_settings?.deduct_transaction_fees_from_refunds
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-muted-foreground" />
            <p className="text-muted-foreground">Loading booking details...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error && !booking) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md border-red-200">
          <CardContent className="p-8 text-center">
            <XCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
            <h2 className="font-heading text-xl font-bold mb-2">Unable to Load Booking</h2>
            <p className="text-muted-foreground mb-4">{error}</p>
            <p className="text-sm text-muted-foreground">
              Please contact the restaurant directly if you need assistance.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (cancelled) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div 
              className="h-16 w-16 rounded-full flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: `${primaryColor}15` }}
            >
              <CheckCircle className="h-8 w-8" style={{ color: primaryColor }} />
            </div>
            <h2 className="font-heading text-2xl font-bold mb-2">Booking Cancelled</h2>
            <p className="text-muted-foreground mb-4">
              Your reservation has been successfully cancelled.
            </p>

            {refundAmount > 0 && (
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
                <p className="font-semibold text-green-800 mb-1">
                  Refund: {currencySymbol}{refundAmount.toFixed(2)}
                </p>
                <p className="text-sm text-green-700">
                  {deductTransactionFees
                    ? 'This is the amount being returned after deducting the original card processing fee. Please allow 5-10 business days.'
                    : 'Your deposit will be refunded within 5-10 business days.'}
                </p>
              </div>
            )}

            <div className="bg-slate-50 rounded-xl p-6 text-left space-y-3 mb-6">
              <div className="flex items-center gap-3">
                <CalendarIcon className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                <div>
                  <p className="font-medium">
                    {booking && format(new Date(booking.booking_date), 'EEEE, MMMM d, yyyy')}
                  </p>
                  <p className="text-sm text-muted-foreground">at {booking?.booking_time}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                <p>{booking?.party_size} {booking?.party_size === 1 ? 'guest' : 'guests'}</p>
              </div>
              {restaurant && (
                <>
                  <div className="flex items-center gap-3">
                    <MapPin className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    <p className="text-sm">{restaurant.name}</p>
                  </div>
                  <div className="flex items-center gap-3">
                    <Phone className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                    <p className="text-sm">{restaurant.phone}</p>
                  </div>
                </>
              )}
            </div>

            <p className="text-sm text-muted-foreground">
              We hope to welcome you again soon!
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-slate-100 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center border-b border-border/50 pb-6">
          <div 
            className="h-12 w-12 rounded-full flex items-center justify-center mx-auto mb-3"
            style={{ backgroundColor: `${primaryColor}15` }}
          >
            <AlertCircle className="h-6 w-6" style={{ color: primaryColor }} />
          </div>
          <CardTitle className="font-heading text-2xl">Cancel Reservation</CardTitle>
          <CardDescription>
            {restaurant?.name}
          </CardDescription>
        </CardHeader>
        
        <CardContent className="p-6 space-y-6">
          {/* Booking Details */}
          <div className="bg-slate-50 rounded-xl p-5 space-y-4">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
              Booking Details
            </h3>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <CalendarIcon className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                <div>
                  <p className="font-medium">
                    {booking && format(new Date(booking.booking_date), 'EEEE, MMMM d, yyyy')}
                  </p>
                  <p className="text-sm text-muted-foreground">at {booking?.booking_time}</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Users className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                <p>{booking?.party_size} {booking?.party_size === 1 ? 'guest' : 'guests'}</p>
              </div>
              <div className="flex items-center gap-3">
                <Mail className="h-5 w-5 text-muted-foreground flex-shrink-0" />
                <p className="text-sm">{booking?.customer_email}</p>
              </div>
              {booking?.deposit_status === 'paid' && booking?.deposit_amount > 0 && (
                <div className="pt-3 border-t border-slate-200">
                  <p className="text-sm text-muted-foreground mb-1">Deposit Paid</p>
                  <p className="font-semibold text-lg">
                    {currencySymbol}{booking.deposit_amount.toFixed(2)}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Refund policy */}
          {hasPaidDeposit && depositLocked ? (
            <div className="bg-amber-50 border border-amber-300 rounded-lg p-4" data-testid="deposit-locked-notice">
              <h3 className="font-semibold text-amber-900 mb-2 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                Your deposit is non-refundable
              </h3>
              <p className="text-sm text-amber-800 leading-relaxed">
                This booking was moved at your request within the non-refundable period, so your deposit was
                carried over as <strong>non-refundable</strong>. You can still cancel to release the table, but
                the deposit <strong>will not be refunded</strong>. If you keep the booking, it will be deducted
                from your final bill.
              </p>
            </div>
          ) : hasPaidDeposit ? (
            <div className={`${refundEligible ? 'bg-blue-50 border-blue-200' : 'bg-amber-50 border-amber-300'} border rounded-lg p-4`}>
              <h3 className={`font-semibold mb-2 flex items-center gap-2 ${refundEligible ? 'text-blue-900' : 'text-amber-900'}`}>
                <AlertCircle className="h-4 w-4" />
                Cancellation policy
              </h3>
              {refundEligible ? (
                <p className="text-sm text-blue-800 leading-relaxed">
                  If you cancel now, your deposit will be{' '}
                  <strong>{deductTransactionFees ? 'refunded minus the original card processing fee' : 'refunded in full'}</strong>
                  {' '}(usually within 5–10 business days).
                  {refundDeadlineText && <> This applies until <strong>{refundDeadlineText}</strong>.</>}
                  {' '}After that, cancelling, or asking us to move the booking, means the deposit is{' '}
                  <strong>non-refundable</strong>.
                </p>
              ) : (
                <p className="text-sm text-amber-800 leading-relaxed">
                  {booking?.refund_status_reason === 'non_refundable'
                    ? <>The deposit for this booking is <strong>non-refundable</strong>.</>
                    : <>Your booking is within {refundHours} hours of your table time, so the deposit is <strong>non-refundable</strong>.</>}
                  {' '}You can still cancel to release the table, but the deposit will not be refunded.
                </p>
              )}
            </div>
          ) : (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h3 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                <AlertCircle className="h-4 w-4" />
                Cancellation policy
              </h3>
              <p className="text-sm text-blue-800 leading-relaxed">
                You can cancel your reservation at any time.
              </p>
            </div>
          )}

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-sm text-red-800">{error}</p>
            </div>
          )}

          {/* Contact Info */}
          <div className="text-center text-sm text-muted-foreground">
            <p>Questions? Contact {restaurant?.name}</p>
            <p className="font-medium mt-1">{restaurant?.phone}</p>
          </div>

          {/* Action Buttons */}
          <div className="flex flex-col gap-3 pt-4">
            <Button
              onClick={handleCancelBooking}
              disabled={cancelling}
              variant="destructive"
              className="w-full"
              size="lg"
            >
              {cancelling ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Cancelling...
                </>
              ) : (
                <>
                  <XCircle className="h-4 w-4 mr-2" />
                  Yes, Cancel My Booking
                </>
              )}
            </Button>
            <Button
              onClick={() => window.close()}
              variant="outline"
              className="w-full"
              disabled={cancelling}
            >
              Never Mind, Keep My Booking
            </Button>
          </div>

          <p className="text-xs text-center text-muted-foreground">
            Confirmation #{booking?.id.slice(0, 8).toUpperCase()}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
